// Die eine Wahrheit ueber die geladenen Sprites: Dateiname, Texturschluessel,
// Raster, Kantenlaenge (VIEW-001, Gate FG-40).
//
// Sie sind die **einzige** Ausnahme von „alles entsteht zur Laufzeit": Schiff,
// Bauteile der Basis, Brocken und Erzadern kommen als PNG aus
// `frontend/public/sprites/`, alles andere — Icons, Nebel, Sterne, Erz-Drops,
// Drohne, Rakete, Partikel und saemtlicher Ton — bleibt prozedural.
//
// Zwei Fortschrittsachsen, zwei Bildersaetze: **die Basis zeigt den Ausbau, das
// Schiff das Level.** Vorher hingen beide am Ausbau, und das Level war nirgends
// zu sehen.
//
// Diese Datei importiert **kein Phaser**. Das ist Absicht: so kann ein Test sie
// lesen und gegen das Dateisystem halten. Ein Tippfehler im Dateinamen waere
// sonst ein stiller Fehler — Phaser laedt nichts, der prozedurale Rueckfall
// greift, und niemand merkt, dass das Bild nie erscheint. Dasselbe Motiv wie
// FG-25 beim Icon-Namen.
//
// **PNG und Rueckfall teilen sich denselben Schluessel.** Die Szene erfaehrt nie,
// welcher der beiden Faelle vorliegt; ohne diese Regel gaebe es zwei
// Toenungsregeln, und die selten ausgefuehrte liefe still auseinander.
//
// **Brocken und Adern liegen als Raster in je einer Datei**, nicht einzeln.
// Phaser haelt nur 16 bis 32 Texturen gleichzeitig gebunden; vierzig einzelne
// Brockentexturen liessen den Zeichenstapel mitten im Feld wiederholt aufbrechen
// — genau der Posten, den der Umbau weg vom Shader einsparen sollte.
//
// **Bei den Gegnern faellt die Entscheidung andersherum: ein Blatt je Art.** Der
// Grund ist die Zahl der Motive — 502 Frames aus **einem** Modellaufruf gibt es
// nicht, und schon 48 Zellen sind an der Grenze. Gebunden wird davon ohnehin nur,
// was im Bild steht: hoechstens drei Gegner je Chunk, also ein paar Arten.
//
// Diese Datei liest `data/balancing` — beide liegen in Schicht 2, das ist
// erlaubt (`core/layers.ts`). Sie nimmt dabei ausdruecklich den **eingebauten
// Abzug** und nicht `balancing()`: `BootScene.preload` laeuft vor dem
// Sitzungsaufbau, die Tabellen vom Server gibt es dort noch nicht.

import { OFFLINE_BALANCING } from '../data/balancing';

/**
 * Level mit eigenem Schiffsbild. Darueber hinaus klemmt es.
 *
 * Fuenfzig ist keine willkuerliche Zahl: die Perk-Maxima summieren sich auf 56
 * Punkte bei einem Punkt je Aufstieg. Darueber ist ein Level nur noch eine Zahl,
 * und die XP-Kurve (`100 * 1,75^(level-1)`) macht jede weitere Stufe ohnehin
 * unerreichbar. Ein Cap gibt es im Regelwerk bewusst nicht — hier steht deshalb
 * eine Grenze der **Darstellung**, keine des Spiels.
 */
export const SHIP_LEVELS = 50;

/** Blaetter zu je zehn Stufen. Alle zehn Level wechselt sichtbar die Schiffsklasse. */
export const SHIP_CLASSES = 5;

/** Kantenlaenge eines Schiffsframes. */
export const SHIP_FRAME = 64;

/** Raster eines Schiffsblattes — dieselben Zahlen wie in der Sheet-Tabelle. */
export const SHIP_COLS = 5;
export const SHIP_ROWS = 2;

/** Stufen je Klasse. */
export const SHIP_PER_CLASS = SHIP_COLS * SHIP_ROWS;

/** Texturschluessel des Bauteil-Rasters. */
export const BASE_SHEET_KEY = 'base';

/** Kantenlaenge eines Bauteilframes. */
export const BASE_FRAME = 128;

/**
 * Raster des Bauteilbildes — 16 Zellen fuer 13 Ausbaustufen.
 *
 * Dreizehn ist kein Rechteck, und ein Raster will voll sein. Die drei uebrigen
 * Frames sind Platz fuer die naechsten Stufen; welcher Frame zu welcher Stufe
 * gehoert, steht in `gfx/base.ts` und wird von FG-45 auf Eindeutigkeit geprueft.
 */
export const BASE_COLS = 4;
export const BASE_ROWS = 4;

/** Plaetze im Bauteilraster. */
export const BASE_FRAMES = BASE_COLS * BASE_ROWS;

/** Texturschluessel des Brocken-Rasters. */
export const ROCK_SHEET_KEY = 'rocks';

/** Texturschluessel des Adern-Rasters. */
export const VEIN_SHEET_KEY = 'veins';

/** Kantenlaenge eines Frames in beiden Rastern. */
export const ROCK_FRAME = 128;
export const VEIN_FRAME = 128;

/** Raster des Brockenbildes — dieselben Zahlen wie in der Sheet-Tabelle in `SPRITES.md`. */
export const ROCK_COLS = 7;
export const ROCK_ROWS = 4;

/** Raster des Adernbildes. */
export const VEIN_COLS = 4;
export const VEIN_ROWS = 4;

/** Formvarianten eines Brockens. Mit Drehung und Spiegelung sind es 112 Erscheinungen. */
export const ROCK_VARIANTS = ROCK_COLS * ROCK_ROWS;

/** Adernbilder von grob nach fein. Welches ein Erz bekommt, sagt `gfx/asteroid.ts`. */
export const VEIN_VARIANTS = VEIN_COLS * VEIN_ROWS;

/**
 * Wie weit die Adern **innerhalb** der Silhouette liegen.
 *
 * Beide Frames sind auf ihre Zelle normiert, aber ein Brocken ist selten rund:
 * er fuellt seine Zelle in der **langen** Achse und laesst in der kurzen Rand.
 * Das Adernnetz ist dagegen kreisrund, also entscheidet die **kurze** Achse des
 * flachsten Umrisses. Gemessen ueber alle 28 Frames: der flachste (Nr. 10) hat
 * 66 von 128 Pixeln, also 0,52 — bei 0,82 standen die Adernspitzen quer dazu
 * sichtbar im leeren Raum neben dem Brocken.
 *
 * 0,5 laesst darunter noch Luft, weil eine Silhouette kein Rechteck ist: an
 * ihren senkrechten Enden ist sie schmaler als ihre Bounding-Box.
 */
export const VEIN_INSET = 0.5;

/**
 * Raster und Kantenlaenge je Gegnerart — eine **Bildtatsache**, keine Regel.
 *
 * Sie steht hier und nicht in `balancing/tables.py`, weil das Backend sie nie
 * liest: sie sagt nur, wie das Modell dieses eine Blatt gezogen hat. Genau
 * deshalb ist sie auch nicht einheitlich — Leerlaeufer und Kuppelwaechter kamen
 * als 8x7 zurueck, das Modell trifft die Zellenzahl nicht zuverlaessig, und die
 * Tabelle folgt in solchen Faellen dem Bild (`SPRITES.md`). Wer hier raet statt
 * nachzusehen, schneidet quer durch die Motive.
 *
 * Die Kolosse tragen 128 statt 64: gezeichnet wird mit `HOSTILE_DRAW * r`, beim
 * Archivar (`r = 66`) also 151 Pixel — aus 64 hochskaliert waere das Matsch.
 *
 * FG-40 haelt diese Karte in **beide** Richtungen gegen die Gegnertabelle: eine
 * Art ohne Blatt waere unsichtbar, ein Blatt ohne Art eine tote Datei. Dieselbe
 * Pruefung wie FG-45 bei den Bauteilen der Basis.
 */
export const ENEMY_SHEETS: Readonly<Record<string, readonly [number, number, number]>> = {
  // Akt I
  scrap: [8, 6, 64],
  splinter: [8, 6, 64],
  warden: [8, 6, 64],
  hunter: [8, 6, 64],
  swarmmother: [4, 4, 128],
  // Akt II
  turret: [8, 6, 64],
  husk: [8, 7, 64],
  sentinel: [8, 7, 64],
  bastion: [8, 6, 64],
  archivist: [4, 4, 128],
  // Der Waechter des tiefen Raums — wie die uebrigen Kolosse wenige Varianten:
  // ein Boss in achtundvierzig Ausfuehrungen ist keiner mehr.
  deepwarden: [4, 4, 128],
};

/**
 * Gezeichnete Breite je Radius.
 *
 * Der Wert stammt aus der Fassung davor und ist absichtlich unveraendert: dort
 * war die Textur 16 Pixel breit und wurde mit `r / 7` skaliert. Wer ihn anfasst,
 * aendert die Groesse **jedes** Gegners im Spiel, nicht nur sein Bild.
 */
export const HOSTILE_DRAW = 16 / 7;

/** Texturschluessel eines Gegnerblattes: `enemy:scrap` … */
export const enemySheetKey = (kindId: string): string => `enemy:${kindId}`;

/**
 * Der prozedurale Rueckfall fuer eine Art **ohne** Blatt.
 *
 * Er ist kein Ersatzsymbol wie das Schloss bei den Icons, sondern die Antwort
 * auf einen Server, der dem Client voraus ist: eine neue Gegnerart erscheint
 * dann als schlichter Rumpf statt gar nicht. Nichts zu zeichnen waere hier
 * schlimmer als bei einem Bauteil (FG-45) — ein unsichtbarer Gegner beschiesst
 * einen trotzdem.
 */
export const ENEMY_FALLBACK_KEY = 'enemy';
export const ENEMY_FALLBACK_FRAME = 32;

/** Texturschluessel und Raster des Drohnenblattes. */
export const DRONE_SHEET_KEY = 'drones';
export const DRONE_FRAME = 64;

/**
 * Zehn Spalten, fuenf Zeilen: **Zeile ist die Schiffsklasse, Spalte der Platz im
 * Schwarm.**
 *
 * Zehn ist der Hoechststand des Schwarms — `drone_unlock` gibt zwei,
 * `drone_count` und `reso_count` je vier. Damit hat jeder Platz sein eigenes
 * Bild, und der Schwarm waechst sichtbar mit dem Schiff, dem er folgt.
 */
export const DRONE_COLS = 10;
export const DRONE_ROWS = 5;

/**
 * Wie stark ein Drohnenframe verkleinert wird.
 *
 * Die Frames sind 64 Pixel breit wie die des Schiffs, eine Drohne ist im Spiel
 * aber ein Punkt neben ihm. Das Blatt ist `gemeinsam` normiert, also traegt
 * dieser eine Faktor beide Enden: die leichteste Klasse landet bei rund zwoelf
 * Bildpunkten, die schwerste bei rund dreiundzwanzig.
 */
export const DRONE_SCALE = 0.4;

/** Texturschluessel und Raster des Sondenblattes. */
export const PROBE_SHEET_KEY = 'probes';
export const PROBE_FRAME = 64;
export const PROBE_COLS = 4;
export const PROBE_ROWS = 2;

/** Texturschluessel und Raster der Gegnergeschosse. */
export const BOLT_SHEET_KEY = 'bolts';
export const BOLT_FRAME = 32;
export const BOLT_COLS = 4;
export const BOLT_ROWS = 3;

/**
 * Wie stark ein Geschossframe verkleinert wird.
 *
 * Die Frames sind 32 Pixel breit, damit beim Schneiden Zeichnung uebrig bleibt;
 * gezeichnet gehoeren sie in die Groessenordnung des alten Strichs (12 Pixel).
 * Ungetrimmt waere ein Schuss groesser als die Schrottdrohne, die ihn abfeuert.
 */
export const BOLT_SCALE = 0.4;

/** Verzeichnis unter `frontend/public/`, aus dem der Browser laedt. */
export const SPRITE_DIR = 'sprites';

export interface SpriteEntry {
  /** Texturschluessel in Phaser. */
  key: string;
  /** Pfad relativ zur Auslieferungswurzel. */
  path: string;
  /** Kantenlaenge **eines Frames** in Pixeln — FG-40 haelt die Datei daran fest. */
  size: number;
  /** Rasterbreite. Fehlt sie, ist der Eintrag ein Einzelbild. */
  cols?: number;
  /** Rasterhoehe. */
  rows?: number;
}

/** Texturschluessel einer Schiffsklasse: `ship:a` … `ship:e`. */
export const shipClassKey = (klasse: number): string =>
  `ship:${String.fromCharCode(97 + Math.max(0, Math.min(SHIP_CLASSES - 1, Math.floor(klasse))))}`;

/**
 * Welches Bild ein Schiff auf diesem Level traegt.
 *
 * **Das Schiff zeigt das Level, die Basis den Ausbau.** Vorher hing es an
 * `save.built` — und weil `built` bis 13 laeuft, es aber nur sechs Bilder gab,
 * sahen neun von vierzehn Ausbauzustaenden gleich aus. Das Level war dagegen
 * ueberhaupt nicht zu sehen.
 *
 * Und es macht den Mitspieler ehrlich: der Server schickt nach FG-32 nur `lvl`,
 * also war die Ausbaustufe eines anderen frueher **geraten** (`floor(lvl/3)`,
 * gesaettigt ab Level 15). Jetzt gibt es nichts mehr zu raten.
 *
 * Level beginnt bei 1. Alles darunter und alles ueber `SHIP_LEVELS` klemmt —
 * ein Frame jenseits des Rasters zeichnete gar nichts, ohne Fehlermeldung.
 */
export function shipLook(level: number): { key: string; frame: number } {
  const stufe = Math.max(1, Math.min(SHIP_LEVELS, Math.floor(level))) - 1;
  return { key: shipClassKey(Math.floor(stufe / SHIP_PER_CLASS)), frame: stufe % SHIP_PER_CLASS };
}

/**
 * Darstellungsgroesse des Schiffs auf diesem Level.
 *
 * Das Wachstum ueber alle 50 Stufen traegt **das Spiel**, nicht das Bild: die
 * fuenf Blaetter sind je Klasse auf ihre groesste Bounding-Box normiert, also
 * fuellt der Shuttle von Stufe 10 seine Zelle genauso wie der schwere Kreuzer
 * von Stufe 50. Ohne diesen Faktor waere jede Klasse gleich gross, und der
 * Aufstieg saehe nach Austausch statt nach Zuwachs aus.
 *
 * Die Spanne ist so gewaehlt, dass Stufe 1 so gross ist wie das frueherere
 * Schiff (48 px x 0,83) und Stufe 50 rund das Anderthalbfache misst.
 */
export function shipRenderScale(level: number): number {
  const t = (Math.max(1, Math.min(SHIP_LEVELS, Math.floor(level))) - 1) / (SHIP_LEVELS - 1);
  return 0.62 + t * 0.53;
}

/** Streufunktion ueber eine Weltobjekt-ID. Alle Ableitungen unten lesen aus ihr. */
function hashOf(rockId: string): number {
  let h = 0;
  for (let i = 0; i < rockId.length; i++) h = (Math.imul(h, 31) + rockId.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Eine von `count` Varianten aus einer stabilen ID — der gemeinsame Weg fuer
 * Brocken, Gegner und Sonden.
 *
 * Es ist **eine** Funktion und nicht drei, weil drei still auseinanderliefen.
 * Und sie ist aus der ID abgeleitet und nicht gezogen: derselbe Gegner sieht
 * nach jedem Neuladen gleich aus, ohne dass ein Formfeld ueber die Leitung geht
 * (Gate FG-32).
 */
export const variantIndex = (id: string, count: number): number =>
  count > 0 ? hashOf(id) % count : 0;

/**
 * Formvariante eines Brockens aus seiner ID — stabil ohne Uebertragung.
 *
 * Derselbe Brocken sieht nach jedem Neuladen gleich aus, und beide Seiten kommen
 * ohne ein Formfeld im Protokoll aus.
 */
export const variantOf = (rockId: string): number => variantIndex(rockId, ROCK_VARIANTS);

/**
 * Spiegelung eines Brockens: Bit 0 waagerecht, Bit 1 senkrecht.
 *
 * Vervierfacht die Zahl der Umrisse, ohne ein einziges zusaetzliches Bild. Sie
 * kommt aus einer **anderen** Stelle des Hashs als die Form — sonst traegen
 * gleiche Formen immer dieselbe Spiegelung, und der Gewinn waere keiner.
 */
export const flipOf = (rockId: string): number => (hashOf(rockId) / ROCK_VARIANTS) & 3;

/**
 * Welches Bild ein Gegner traegt: Art waehlt das Blatt, ID die Variante.
 *
 * Die Variantenzahl kommt aus **diesem** Blatt, nicht aus einer Konstanten fuer
 * alle: Leerlaeufer und Kuppelwaechter haben 56 Frames, der Rest 48, die Kolosse
 * 16. Eine gemeinsame Zahl zeigte fuer acht Arten auf Frames, die es nicht gibt
 * — und Phaser zeichnet einen Frame jenseits des Rasters ersatzlos **nicht**.
 */
export function enemyLook(
  kindId: string,
  hostileId: string,
): { key: string; frame: number; frameSize: number } {
  const blatt = ENEMY_SHEETS[kindId];
  if (!blatt) {
    return { key: ENEMY_FALLBACK_KEY, frame: 0, frameSize: ENEMY_FALLBACK_FRAME };
  }
  const [spalten, zeilen, kante] = blatt;
  return {
    key: enemySheetKey(kindId),
    frame: variantIndex(hostileId, spalten * zeilen),
    frameSize: kante,
  };
}

/**
 * Darstellungsgroesse eines Gegners aus seinem Radius.
 *
 * `r` ist eine Zahl je Art, also steht die Skala beim Anlegen des Sprites fest
 * und muss nicht je Bild gerechnet werden.
 */
export const hostileRenderScale = (r: number, frameSize: number): number =>
  (r * HOSTILE_DRAW) / frameSize;

/**
 * Welches Bild eine Begleitdrohne traegt: Zeile ist die Schiffsklasse, Spalte
 * der Platz im Schwarm.
 *
 * Der Platz wird geklemmt und nicht umgebrochen: waechst der Schwarm eines Tages
 * ueber zehn, sollen die letzten gleich aussehen und nicht wieder wie die
 * ersten — ein Zwilling in derselben Formation faellt auf, ein Nachzuegler nicht.
 */
export function droneLook(shipLevel: number, slot: number): { key: string; frame: number } {
  const klasse = Math.floor(
    (Math.max(1, Math.min(SHIP_LEVELS, Math.floor(shipLevel))) - 1) / SHIP_PER_CLASS,
  );
  const platz = Math.max(0, Math.min(DRONE_COLS - 1, Math.floor(slot)));
  return {
    key: DRONE_SHEET_KEY,
    frame: Math.min(DRONE_ROWS - 1, klasse) * DRONE_COLS + platz,
  };
}

/** Welches Bild eine Sonde traegt — Variante aus ihrer ID, wie beim Brocken. */
export const probeLook = (probeId: string): { key: string; frame: number } => ({
  key: PROBE_SHEET_KEY,
  frame: variantIndex(probeId, PROBE_COLS * PROBE_ROWS),
});

/**
 * Welches Geschossbild eine Gegnerart abfeuert.
 *
 * Die Position in der Tabelle, nicht ein neues Tabellenfeld: der Frame ist eine
 * Bildtatsache wie das Blatt selbst, und ein Feld dafuer waere ein String, den
 * das Backend nie liest (dieselbe Ueberlegung wie bei `ENEMY_SHEETS`).
 */
export function boltFrameOf(kindId: string): number {
  const platz = OFFLINE_BALANCING.enemies.findIndex((art) => art.id === kindId);
  return platz < 0 ? 0 : platz % (BOLT_COLS * BOLT_ROWS);
}

/** Texturschluessel des Fundraster — Kisten **und** Fundstuecke in einem Blatt. */
export const FIND_SHEET_KEY = 'finds';

/** Kantenlaenge eines Fundframes. */
export const FIND_FRAME = 64;

export const FIND_COLS = 3;
export const FIND_ROWS = 2;

/**
 * Welches Bild eine Fundstelle traegt — ihre **Tabellenposition**.
 *
 * Dieselbe Ueberlegung wie beim Bauteil der Basis (FG-45) und beim
 * Gegnergeschoss: der Frame ist eine Bildtatsache, kein Tabellenfeld. Ein
 * `sprite`-String in `tables.py` waere ein Wert, den das Backend nie liest und
 * der nur durch den Abzug wandert.
 *
 * Und er ist **eindeutig je Fund**: fuenf Kisten, fuenf Bilder. Vorher trugen
 * alle dieselbe prozedurale blaue Kiste, obwohl die Namen eine Bake, einen
 * Kanister, einen Wandschrank und vier Container beschreiben — wer 20 000 Meter
 * nach einem Hinweis fliegt, soll am Bild sehen, dass er richtig ist.
 */
export function cacheFrameOf(cacheId: string): number {
  const platz = OFFLINE_BALANCING.caches.findIndex((c) => c.id === cacheId);
  return platz < 0 ? 0 : platz % (FIND_COLS * FIND_ROWS);
}

/**
 * Welches Bild ein Fundstueck traegt — **hinter** den Kisten im selben Blatt.
 *
 * Ein eigenes Blatt fuer ein einziges Bild waere eine Textur mehr im Speicher
 * und eine Datei mehr im Manifest; Phaser haelt nur 16 bis 32 gleichzeitig
 * gebunden (`darstellung.md`). Kisten und Geraete stehen ohnehin nie ohne
 * einander im Bild.
 */
export function gearFrameOf(gearId: string): number {
  const platz = OFFLINE_BALANCING.gear.findIndex((g) => g.id === gearId);
  const versatz = OFFLINE_BALANCING.caches.length;
  return platz < 0 ? versatz : (versatz + platz) % (FIND_COLS * FIND_ROWS);
}

function shipEntries(): SpriteEntry[] {
  const out: SpriteEntry[] = [];
  for (let klasse = 0; klasse < SHIP_CLASSES; klasse++) {
    out.push({
      key: shipClassKey(klasse),
      path: `${SPRITE_DIR}/ship-${String.fromCharCode(97 + klasse)}.png`,
      size: SHIP_FRAME,
      cols: SHIP_COLS,
      rows: SHIP_ROWS,
    });
  }
  return out;
}

/**
 * Ein Eintrag je Gegnerart — aus der **Tabelle** gelaufen, nicht von Hand.
 *
 * Zehn Zeilen hinzuschreiben waere die zweite Wahrheit, die still veraltet:
 * genau so hat `HOSTILE_TINT` die fuenf Arten von Akt II verloren. So macht eine
 * neue Zeile in `tables.py` das Gate rot, bis ihr Blatt existiert.
 */
function enemyEntries(): SpriteEntry[] {
  const out: SpriteEntry[] = [];
  for (const art of OFFLINE_BALANCING.enemies) {
    const blatt = ENEMY_SHEETS[art.id];
    if (!blatt) continue;
    const [cols, rows, size] = blatt;
    out.push({ key: enemySheetKey(art.id), path: `${SPRITE_DIR}/enemy-${art.id}.png`, size, cols, rows });
  }
  return out;
}

/** Alles, was `BootScene.preload` laedt — und was FG-40 gegen die Platte haelt. */
export const SPRITE_MANIFEST: readonly SpriteEntry[] = [
  ...shipEntries(),
  ...enemyEntries(),
  {
    key: DRONE_SHEET_KEY,
    path: `${SPRITE_DIR}/drones.png`,
    size: DRONE_FRAME,
    cols: DRONE_COLS,
    rows: DRONE_ROWS,
  },
  {
    key: PROBE_SHEET_KEY,
    path: `${SPRITE_DIR}/probes.png`,
    size: PROBE_FRAME,
    cols: PROBE_COLS,
    rows: PROBE_ROWS,
  },
  {
    key: BOLT_SHEET_KEY,
    path: `${SPRITE_DIR}/bolts.png`,
    size: BOLT_FRAME,
    cols: BOLT_COLS,
    rows: BOLT_ROWS,
  },
  {
    key: BASE_SHEET_KEY,
    path: `${SPRITE_DIR}/base.png`,
    size: BASE_FRAME,
    cols: BASE_COLS,
    rows: BASE_ROWS,
  },
  {
    key: ROCK_SHEET_KEY,
    path: `${SPRITE_DIR}/rocks.png`,
    size: ROCK_FRAME,
    cols: ROCK_COLS,
    rows: ROCK_ROWS,
  },
  {
    key: VEIN_SHEET_KEY,
    path: `${SPRITE_DIR}/veins.png`,
    size: VEIN_FRAME,
    cols: VEIN_COLS,
    rows: VEIN_ROWS,
  },
  {
    key: FIND_SHEET_KEY,
    path: `${SPRITE_DIR}/finds.png`,
    size: FIND_FRAME,
    cols: FIND_COLS,
    rows: FIND_ROWS,
  },
];
